import { Component, OnInit, AfterViewInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { GoalType, PPType } from '../../interfaces/Bounty';
import { BountyService } from '../../services/bounty.service';
import { UserService } from 'src/app/services/user.service';

export type GameMode = 'osu' | 'taiko' | 'ctb' | 'mania';
export const GAME_MODES: GameMode[] = ['osu', 'taiko', 'ctb', 'mania'];
const objectToArray = (object: object): any[] => {
  return Object.entries(object)
    .map(([key, val]) => {
      return val ? key : null;
    })
    .filter(value => !!value);
};

const parseReward = (amount: number): number => {
  return parseFloat(amount.toFixed(2));
};

export const transformFormDataToBountyRequest = (formData) => ({
  ...formData,
  beatmapGoals:
    formData.beatmapGoals && objectToArray(formData.beatmapGoals),
  mods: formData.mods && objectToArray(formData.mods),
  duration: formData.duration && formData.duration.id,
  reward: parseReward(formData.reward) * 100,
  ppType: formData.ppType && formData.ppType.value,
  paymentType: formData.isPool ? 'pool' : undefined,
});
export const transformValidationErrors = (errors): string[] => <string[]>Object.values(errors).reduce(
  (acc: any[], curr: any[]) => {
    acc.push(...curr);

    return acc;
  },
  [],
);

export const durationOptions = [
  { id: 24, name: '1 day' },
  { id: 3 * 24, name: '3 days' },
  { id: 7 * 24, name: '1 week' },
  { id: 30 * 24, name: '30 days' },
  { id: 90 * 24, name: '90 days' },
];

@Component({
  selector: 'app-create-bounty',
  templateUrl: './create-bounty.component.html',
  styleUrls: ['./create-bounty.component.scss'],
})
export class CreateBountyComponent implements OnInit, AfterViewInit {
  public form: FormGroup;
  public durationOptions = durationOptions;
  public duration: { name: string; id: number } = this.durationOptions.find(
    o => o.name === '30 days',
  ); // Duration of hours until bounty ends
  public goalType: GoalType;
  public beatmapId: number;
  public ppTypes: { value: PPType; label: string }[] = [
    { label: 'PP from a single score', value: 'single' },
    { label: 'Total PP', value: 'total' },
  ];
  public ppType = this.ppTypes[0];
  public gameModes = GAME_MODES;
  public gameMode: GameMode = 'osu';
  public modeLabels = {
    osu: 'osu! Standard',
    taiko: 'Taiko',
    ctb: 'Catch the Beat',
    mania: 'Mania',
  };

  public ppAmount: number;
  public tournamentName: string;
  public tournamentPlace: number;
  public acceptTerms = false;
  public submitFailed = false;
  public errors: string[];
  public submitting = false;
  public success = false;
  public requiresPayment = false;
  public bounty: { id: number };
  public user: any;
  public parseReward = parseReward;

  constructor(private bountyService: BountyService, userService: UserService) {
    this.user = userService.getUser();
  }

  ngOnInit() {
    this.form = new FormGroup({
      goalType: new FormControl(this.goalType, [Validators.required]),
      beatmapId: new FormControl(this.beatmapId),
      ppType: new FormControl(this.ppType),
      gameMode: new FormControl(this.gameMode, [Validators.required]),
      ppAmount: new FormControl(this.ppAmount),
      tournamentName: new FormControl(this.tournamentName),
      tournamentPlace: new FormControl(this.tournamentPlace),
      duration: new FormControl(this.duration, [
        Validators.required,
        Validators.min(1),
        Validators.max(8760),
      ]),
      acceptTerms: new FormControl(this.acceptTerms, [Validators.requiredTrue]),
    });

    this.updateValidators(this.form.get('goalType').value, true);
  }

  ngAfterViewInit() { }

  public changeGoalType(goalType: GoalType) {
    this.form.patchValue({ goalType });
    this.updateValidators(goalType);
    this.goalType = goalType;
  }

  public maybeToggleDifficultyReduction() {
    const selectedMods = this.form.controls.mods.value;
    const easierModSelected = !!(
      selectedMods.ez ||
      selectedMods.rx ||
      selectedMods.nf ||
      selectedMods.ht
    );

    this.form.patchValue({
      beatmapGoals: {
        no_difficulty_reduction: !easierModSelected,
      },
    });
  }

  // Sets and updates all validators (e.g. when goal type is changed)
  private updateValidators(goalType: GoalType, forceUpdate?: boolean) {
    if (goalType !== this.goalType || forceUpdate) {
      this.setBeatmapValidators(goalType === 'beatmap');
      this.setPpValidators(goalType === 'pp');
      this.setTournamentValidators(goalType === 'tournament');
      // this.setMissesValidators(goalType);
      // this.setAccuracyValidators(goalType);
      // this.setModValidators(goalType);
      // this.setCustomRuleValidator();
    }
  }

  private setBeatmapValidators(isActive) {
    if (isActive) {
      this.form.controls.beatmapId.setValidators([
        Validators.min(1),
        Validators.max(999999999999999),
        Validators.required,
      ]);
      // this.form.controls.beatmapGoals.setValidators([atLeastOne()]);
    } else {
      this.form.controls.beatmapId.clearValidators();
      // this.form.controls.beatmapGoals.clearValidators();
    }
    this.form.controls.beatmapId.updateValueAndValidity();
    // this.form.controls.beatmapGoals.updateValueAndValidity();
  }

  private setPpValidators(isActive) {
    if (isActive) {
      this.form.controls.ppType.setValidators([Validators.required]);
      this.form.controls.ppAmount.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(20000),
      ]);
    } else {
      this.form.controls.ppType.clearValidators();
      this.form.controls.ppAmount.clearValidators();
    }
    this.form.controls.ppType.updateValueAndValidity();
    this.form.controls.ppAmount.updateValueAndValidity();
  }

  private setTournamentValidators(isActive) {
    if (isActive) {
      this.form.controls.tournamentName.setValidators([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(80),
      ]);
      this.form.controls.tournamentPlace.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(128),
      ]);
    } else {
      this.form.controls.tournamentName.clearValidators();
      this.form.controls.tournamentPlace.clearValidators();
    }
    this.form.controls.tournamentName.updateValueAndValidity();
    this.form.controls.tournamentPlace.updateValueAndValidity();
  }

  public isGoalType(goalType: GoalType) {
    return this.form.get('goalType').value === goalType;
  }

  // public getFormValidationErrors() {
  //   Object.keys(this.form.controls).forEach(key => {
  //     const controlErrors: ValidationErrors = this.form.get(key).errors;
  //     if (controlErrors) {
  //       console.log(key, controlErrors);
  //     }
  //   });
  // }

  public hasError(controlKey: string) {
    const control = this.form.get(controlKey);
    const touchedOrSubmitted = !control.pristine || this.submitFailed;
    return control.errors && touchedOrSubmitted;
  }

  public async submitForm() {
    if (!this.form.valid) {
      this.submitFailed = true;

      return;
    }

    this.submitting = true;
    this.errors = undefined;

    const formData = this.form.value;

    // Transform to correct format for request
    const dataTransformed = transformFormDataToBountyRequest(formData);

    try {
      const { bounty, requiresPayment } = await this.bountyService.create(dataTransformed);
      this.bounty = bounty;
      this.requiresPayment = requiresPayment;
      this.success = true;
    } catch (e) {
      if (e.error && e.error.errors) {
        this.errors = transformValidationErrors(e.error.errors);
      } else {
        const error =
          e.status === 401
            ? 'You are not logged in. Please log in and try again.'
            : 'A server error occured';
        this.errors = [error];
      }
    }

    this.submitting = false;
  }

}
